import { z } from "zod";
import { agentBranchSchema, hubRepositoryIdSchema } from "./agent-tasks";
import { createWorkItemSchema } from "./work-items";

/**
 * A task the caller asks Zot to start. The composer carries no options: the caller sends only
 * the prompt, and the backend picks the repository, branch, model, and suggested cap when the
 * caller confirms. The estimate range is display-only; the reserved cap is what the run holds.
 * Nothing starts before confirm: the executor runs this only through `POST /zot/confirm`.
 */
export const startAgentTaskProposalSchema = z
  .object({
    prompt: z.string().trim().min(1).max(20_000),
    repositoryId: hubRepositoryIdSchema.optional(),
    branch: agentBranchSchema.optional(),
    creditCap: z.number().int().min(1).optional(),
    estimateMinCredits: z.number().int().nonnegative(),
    estimateMaxCredits: z.number().int().nonnegative(),
    /** The cost class the router chose. The hub narrows the profile allowlist to it on confirm. */
    modelTier: z.enum(["lookup", "standard", "deep"]).optional(),
    /** When true, confirm uses the project's live model key and reserves no credits. */
    byok: z.boolean().optional(),
  })
  .refine((input) => input.estimateMinCredits <= input.estimateMaxCredits, {
    message: "The estimate minimum exceeds the maximum.",
  });
export type StartAgentTaskProposal = z.infer<typeof startAgentTaskProposalSchema>;

/** Who is speaking in a Zot thread. Zot is the assistant, never an approver. */
export const zotRoles = ["user", "assistant"] as const;
export const zotRoleSchema = z.enum(zotRoles);
export type ZotRole = z.infer<typeof zotRoleSchema>;

/** One source Zot cited. Citations are links to real records, scoped to the project. */
export const zotCitationSchema = z.object({
  kind: z.enum([
    "run",
    "work-item",
    "artefact",
    "flow",
    "member",
    "ledger-entry",
    "wallet",
    "approval",
    "audit-entry",
    "repository",
    "file",
  ]),
  ref: z.string().min(1),
  label: z.string().min(1),
});
export type ZotCitation = z.infer<typeof zotCitationSchema>;

/**
 * `POST /api/v1/projects/:id/zot/confirm` input for an evidence pack export. The run id names
 * the finished run to pack; the server rebuilds the pack from project records at confirm time,
 * so a tampered client payload can never alter what is exported.
 */
export const buildEvidencePackProposalSchema = z.object({
  runId: z.uuid(),
});
export type BuildEvidencePackProposal = z.infer<typeof buildEvidencePackProposalSchema>;

/** A requested policy cap comes from the caller's text and is checked again at confirmation. */
export const zotProjectCapProposalInputSchema = z.object({
  requestedPerRunCap: z.number().int().min(1).optional(),
  expectedPerRunCap: z.number().int().min(1).optional(),
});
export type ZotProjectCapProposalInput = z.infer<typeof zotProjectCapProposalInputSchema>;

/** Model-suggested run targets are replaced by server-verified flow, work item, and cap data. */
export const zotStartRunProposalInputSchema = z.object({
  workItemId: z.uuid().optional(),
  flowId: z.uuid().optional(),
  flowVersionId: z.uuid().optional(),
  intent: z.string().trim().min(1).max(2000).optional(),
  branch: z.string().trim().min(1).max(120).optional(),
  creditCap: z.number().int().min(1).optional(),
  expectedPerRunCap: z.number().int().min(1).optional(),
});
export type ZotStartRunProposalInput = z.infer<typeof zotStartRunProposalInputSchema>;

/** Run ids from the project run-read tool; the server adds the live Hub id and state version. */
export const zotCancelRunProposalInputSchema = z.object({
  runId: z.uuid(),
  hubRunId: z.uuid().optional(),
  expectedStateVersion: z
    .string()
    .regex(/^[1-9][0-9]{0,17}$/)
    .optional(),
  expectedStatus: z
    .enum([
      "pending",
      "start_blocked",
      "queued",
      "running",
      "waiting_approval",
      "waiting_quota",
      "blocked",
      "cancelling",
    ])
    .optional(),
  reason: z.string().trim().min(1).max(2000).optional(),
});
export type ZotCancelRunProposalInput = z.infer<typeof zotCancelRunProposalInputSchema>;

/**
 * Retry keeps the same run. The server copies the existing credit cap. A caller-supplied cap is
 * optional only so a stored proposal can restate the server figure; it can never raise it.
 */
export const zotRetryRunProposalInputSchema = z.object({
  runId: z.uuid(),
  creditCap: z.number().int().min(1).optional(),
  expectedPerRunCap: z.number().int().min(1).optional(),
  expectedCreditCap: z.number().int().min(1).optional(),
  expectedStateVersion: z
    .string()
    .regex(/^[1-9][0-9]{0,17}$/)
    .optional(),
});
export type ZotRetryRunProposalInput = z.infer<typeof zotRetryRunProposalInputSchema>;

/** A recorded step id. The server refuses any step that is not already on the run. */
const stepKeySchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,79}$/);

/**
 * Rerun from a step starts a new run. The credit cap is the server minimum of the source cap
 * and the live wallet and flow limits. The model does not choose it.
 */
export const zotRerunFromStepProposalInputSchema = z.object({
  runId: z.uuid(),
  stepKey: stepKeySchema,
  creditCap: z.number().int().min(1).optional(),
  expectedPerRunCap: z.number().int().min(1).optional(),
  expectedSourceCap: z.number().int().min(1).optional(),
  expectedStateVersion: z
    .string()
    .regex(/^[1-9][0-9]{0,17}$/)
    .optional(),
});
export type ZotRerunFromStepProposalInput = z.infer<typeof zotRerunFromStepProposalInputSchema>;

/**
 * A state-changing suggestion Zot returns instead of carrying out. Nothing changes state before
 * the caller confirms, so this is a proposal, never an applied edit.
 */
export const changeUsageLimitProposalSchema = z
  .object({
    field: z.enum(["monthly-limit", "per-run-cap"]),
    expectedValue: z.number().int().nonnegative(),
    requestedValue: z.number().int().nonnegative(),
  })
  .refine(
    (input) =>
      input.field !== "per-run-cap" || (input.expectedValue >= 1 && input.requestedValue >= 1),
    {
      message: "A per-run cap must be at least one credit.",
    },
  );
export type ChangeUsageLimitProposal = z.infer<typeof changeUsageLimitProposalSchema>;

/** The step kinds a drafted workflow may use. Tools, skills, deciders, and caps are set by a person. */
export const draftWorkflowStepKinds = ["agent", "planner", "approval", "deploy", "note"] as const;

/**
 * A workflow Zot drafts for the caller. Confirm saves it as a draft flow in the project. It is
 * never published, and it binds no tool, skill, decider, or spend cap: a person sets those in the
 * designer, and activation refuses the draft until they do. Unknown keys, such as a tool or a
 * spend cap, are refused rather than dropped, so a proposal cannot smuggle one through.
 */
export const draftWorkflowProposalInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    steps: z
      .array(
        z
          .object({
            name: z.string().trim().min(1).max(120),
            kind: z.enum(draftWorkflowStepKinds),
            gate: z.boolean().default(false),
          })
          .strict(),
      )
      .min(1)
      .max(20),
  })
  .strict();
export type DraftWorkflowProposalInput = z.infer<typeof draftWorkflowProposalInputSchema>;

/**
 * The object a proposal is bound to. Confirm re-reads this revision and refuses to run when it
 * has moved, so a stale proposal cannot apply against a record the caller has not reviewed.
 */
export const zotProposalBindingSchema = z.object({
  objectKind: z.string().trim().min(1).max(64),
  objectId: z.string().trim().min(1).max(200),
  revision: z.number().int().nonnegative(),
  /** Digest of the full revision snapshot when a single numeric revision is not sufficient. */
  revisionToken: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  /** What the object looked like when the proposal was written. Shown again if it is stale. */
  observed: z.string().trim().min(1).max(500),
});
export type ZotProposalBinding = z.infer<typeof zotProposalBindingSchema>;

export const zotProposalSchema = z.object({
  summary: z.string().trim().min(1).max(500),
  changes: z.array(z.string().trim().min(1).max(500)).min(1).max(12),
  action: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("create-work-item"), input: createWorkItemSchema }),
    z.object({ kind: z.literal("start-agent-task"), input: startAgentTaskProposalSchema }),
    z.object({ kind: z.literal("build-evidence-pack"), input: buildEvidencePackProposalSchema }),
    z.object({ kind: z.literal("start-run"), input: zotStartRunProposalInputSchema }),
    z.object({ kind: z.literal("cancel-run"), input: zotCancelRunProposalInputSchema }),
    z.object({ kind: z.literal("retry-run"), input: zotRetryRunProposalInputSchema }),
    z.object({ kind: z.literal("rerun-from-step"), input: zotRerunFromStepProposalInputSchema }),
    z.object({ kind: z.literal("raise-project-cap"), input: zotProjectCapProposalInputSchema }),
    z.object({ kind: z.literal("change-usage-limit"), input: changeUsageLimitProposalSchema }),
    z.object({ kind: z.literal("draft-workflow"), input: draftWorkflowProposalInputSchema }),
  ]),
  /** Present when the proposal is pinned to an object revision. Absent on legacy proposals. */
  binding: zotProposalBindingSchema.optional(),
  /** Server-set expiry. A proposal past this instant must not run. */
  expiresAt: z.iso.datetime().optional(),
});
export type ZotProposal = z.infer<typeof zotProposalSchema>;

/**
 * The tool families Zot can route to. Each maps to one slash command in the command reference,
 * so the label shown to the caller is the command they can switch to.
 */
export const zotRoutedTools = [
  "answer",
  "room",
  "studio",
  "run",
  "work",
  "approvals",
  "usage",
  "automation",
  "integrations",
  "market",
  "evidence",
  "project",
] as const;
export const zotRoutedToolSchema = z.enum(zotRoutedTools);
export type ZotRoutedTool = z.infer<typeof zotRoutedToolSchema>;

/**
 * The model tier the router chose. Cheap tiers answer lookups, stronger tiers answer analysis,
 * and code work. The tier is a backend decision: the composer never offers a picker.
 */
export const zotModelTiers = ["lookup", "standard", "deep"] as const;
export const zotModelTierSchema = z.enum(zotModelTiers);
export type ZotModelTier = z.infer<typeof zotModelTierSchema>;

/**
 * What the router decided for one message. The caller sees the tool and the model tier so the
 * choice is never a mystery, and can switch to another tool with one click. `clarifyingQuestion`
 * is set only when the request was too ambiguous to route, and then it is one short question.
 */
export const zotRoutingSchema = z.object({
  tool: zotRoutedToolSchema,
  modelTier: zotModelTierSchema,
  /** The customer's own model key is in use, so the model costs the customer nothing. */
  byok: z.boolean().default(false),
  /** The repository the router inferred, when it inferred one from the page, run, or work item. */
  repositoryId: z.string().min(1).nullable().default(null),
  /** The spend cap the router suggests for work that would spend credits. */
  suggestedCap: z.number().int().nonnegative().nullable().default(null),
  /** One short question, set only when the request could not be routed confidently. */
  clarifyingQuestion: z.string().min(1).max(200).nullable().default(null),
  /** Other tools the caller can switch to with one click. */
  alternatives: z.array(zotRoutedToolSchema).max(3).default([]),
  /**
   * How deep the analysis should go. A small request is short, a large one is full.
   * This is an estimate the backend shows, never a composer setting.
   */
  analysisDepth: z.enum(["short", "standard", "full"]).default("standard"),
  /**
   * Whether the selected tool ran. `off` is the default: routing is recorded, and nothing is
   * executed. `executed` means the read ran or a proposal was prepared. `refused` means the
   * tool did not run because the repository, the model, or the caller's authority was absent.
   */
  dispatch: z.enum(["off", "executed", "refused"]).default("off"),
});
export type ZotRouting = z.infer<typeof zotRoutingSchema>;

/**
 * One assistant reply. `proposal` is set only when the answer would change state; the caller
 * must then confirm it. A refusal explains itself in `body` and carries no proposal. Zot never
 * decides an approval and never returns a secret.
 */
export const zotMessageSchema = z.object({
  id: z.uuid(),
  threadId: z.uuid(),
  role: zotRoleSchema,
  body: z.string().min(1),
  citations: z.array(zotCitationSchema).default([]),
  proposal: zotProposalSchema.nullable().default(null),
  proposalState: z.enum(["proposed", "confirming", "confirmed"]).nullable().default(null),
  /** What the router chose for this message, so the caller can see and switch the tool. */
  routing: zotRoutingSchema.nullable().default(null),
  /** The flow a confirmed workflow draft was saved as, so the card can open the designer. */
  draftFlowId: z.uuid().nullish(),
});
export type ZotMessage = z.infer<typeof zotMessageSchema>;

/** The provider-neutral answer shape returned by a configured Zot adapter. */
export const zotAnswerSchema = z.object({
  body: z.string().trim().min(1).max(12000),
  citations: z.array(zotCitationSchema).max(16),
  proposal: zotProposalSchema.nullable(),
  tokens: z
    .object({
      inputTokens: z.number().int().nonnegative(),
      outputTokens: z.number().int().nonnegative(),
    })
    .optional(),
});
export type ZotAnswer = z.infer<typeof zotAnswerSchema>;

export const DEFAULT_FAIR_USE_CAP_TOKENS = 100_000;

export const agentUsageSummarySchema = z.object({
  projectId: z.uuid(),
  modelAccess: z.enum(["credits", "byok"]),
  fairUseCapTokens: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  projectTokens: z.number().int().nonnegative(),
  userTokens: z.number().int().nonnegative(),
  costDisplay: z.string(),
  isCapExceeded: z.boolean(),
});
export type AgentUsageSummary = z.infer<typeof agentUsageSummarySchema>;

const usageCount = z.number().int().nonnegative();

/** Counts for one routed tool, or for one chat, read from the assistant replies in it. */
const zotUsageTotals = {
  calls: usageCount,
  refused: usageCount,
  proposals: usageCount,
  confirmed: usageCount,
  awaiting: usageCount,
  tokens: usageCount,
  creditsSettled: usageCount,
  byokTasks: usageCount,
};

/** `GET /projects/:id/zot/tool-usage`: which assistant tools ran and what their confirmed work cost. */
export const zotToolUsageSchema = z.object({
  projectId: z.uuid(),
  tools: z.array(z.object({ tool: zotRoutedToolSchema, ...zotUsageTotals })),
  chats: z.array(
    z.object({
      threadId: z.uuid(),
      startedAt: z.iso.datetime(),
      mine: z.boolean(),
      ...zotUsageTotals,
    }),
  ),
});
export type ZotToolUsage = z.infer<typeof zotToolUsageSchema>;

export const zotMessageListSchema = z.array(zotMessageSchema);
export type ZotMessageList = z.infer<typeof zotMessageListSchema>;

/** `POST /api/v1/projects/:id/zot/search` finds project records for a question. */
export const zotSearchQuerySchema = z.object({
  query: z.string().trim().min(1).max(500),
  kinds: z
    .array(
      z.enum([
        "run",
        "work-item",
        "artefact",
        "flow",
        "member",
        "ledger-entry",
        "approval",
        "audit-entry",
        "repository",
        "file",
      ]),
    )
    .max(10)
    .optional(),
  limit: z.number().int().min(1).max(20).optional(),
});
export type ZotSearchQuery = z.infer<typeof zotSearchQuerySchema>;

/** One retrieved record: the citation Zot can quote plus the redacted text it grounds. */
export const zotSearchResultSchema = z.object({
  citation: zotCitationSchema,
  snippet: z.string().min(1).max(1000),
});
export type ZotSearchResult = z.infer<typeof zotSearchResultSchema>;

export const zotSearchResponseSchema = z.object({
  results: z.array(zotSearchResultSchema).max(20),
});
export type ZotSearchResponse = z.infer<typeof zotSearchResponseSchema>;

/** Object kinds a contextual Ask button can attach to one chat request. */
export const askZotObjectTypes = ["run", "approval", "flow", "work-item", "usage"] as const;
export const askZotObjectTypeSchema = z.enum(askZotObjectTypes);
export type AskZotObjectType = z.infer<typeof askZotObjectTypeSchema>;

/**
 * The record the caller is asking about. The server resolves it only after membership and
 * role checks, and only inside the project on the path.
 */
export const askZotAboutSchema = z.object({
  type: askZotObjectTypeSchema,
  id: z.string().trim().min(1).max(200),
});
export type AskZotAbout = z.infer<typeof askZotAboutSchema>;

/** `POST /api/v1/projects/:id/zot/messages`. Chats do not spend credits. */
export const askZotSchema = z.object({
  threadId: z.uuid().nullable().optional(),
  message: z.string().trim().min(1).max(4000),
  about: askZotAboutSchema.optional(),
});
export type AskZot = z.infer<typeof askZotSchema>;

/**
 * `POST /api/v1/projects/:id/zot/confirm` carries out one proposal the caller has confirmed.
 * The message id identifies that proposal, so a confirm can never apply a second edit. Extra
 * ids are rejected. `changeIndex` selects one line of a change set; the other lines do not run.
 */
export const confirmZotSchema = z
  .object({
    messageId: z.uuid(),
    changeIndex: z.number().int().min(0).max(11).optional(),
  })
  .strict();
export type ConfirmZot = z.infer<typeof confirmZotSchema>;

export const zotThreadSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  userId: z.uuid(),
  title: z.string().max(120).default(""),
  createdAt: z.string().datetime(),
});
export type ZotThread = z.infer<typeof zotThreadSchema>;

export const zotThreadMessagesSchema = z.object({
  thread: zotThreadSchema,
  messages: zotMessageListSchema,
});
export type ZotThreadMessages = z.infer<typeof zotThreadMessagesSchema>;

/** A stream event emitted while Zot composes an answer. */
export const zotStreamEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("thinking") }),
  z.object({ type: z.literal("token"), text: z.string().min(1).max(4000) }),
  z.object({ type: z.literal("refusal"), reason: z.string().min(1).max(2000) }),
  z.object({ type: z.literal("error"), detail: z.string().min(1).max(2000) }),
  z.object({ type: z.literal("done"), message: zotMessageSchema }),
]);
export type ZotStreamEvent = z.infer<typeof zotStreamEventSchema>;

/** `GET /api/v1/projects/:id/zot/threads` lists the caller's private threads. */
export const zotThreadListSchema = z.array(zotThreadSchema);
export type ZotThreadList = z.infer<typeof zotThreadListSchema>;

/** `PATCH /api/v1/projects/:id/zot/threads/:threadId` renames a thread. */
export const renameZotThreadSchema = z.object({
  title: z.string().trim().min(1).max(120),
});
export type RenameZotThread = z.infer<typeof renameZotThreadSchema>;
